import { useCallback, useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { useAudioPlayback } from './AudioPlayerProvider';
import { formatClock } from '../lib/format';

/**
 * Player de uma gravacao de ligacao da IA (RF07 / RNF17).
 *
 * A regra de exclusividade NAO esta no onClick do botao. Esta no handler do
 * evento 'play' do proprio elemento <audio>, que e o unico ponto por onde todos
 * os caminhos de reproducao passam: o nosso botao, os controles nativos do
 * elemento, as teclas de midia do sistema operacional / fone bluetooth e
 * qualquer chamada programatica a `play()`. Amarrar a exclusividade ao clique
 * deixaria todos os outros caminhos de fora -- e e exatamente por ali que duas
 * gravacoes acabam tocando juntas.
 *
 * `preload="none"` e deliberado: uma pagina com vinte campanhas nao pode
 * disparar vinte downloads de audio ao abrir. Ate o usuario apertar play, a
 * duracao exibida vem do que o backend ja registrou.
 */

interface AudioPlayerProps {
  /** Identificador unico no registro de reproducao. Use o id da campanha. */
  readonly id: string;
  readonly src: string;
  /** Descricao para leitor de tela, ja sem nome completo do lead. */
  readonly label: string;
  /** Duracao conhecida pelo backend, em segundos, usada antes dos metadados. */
  readonly fallbackDurationSeconds?: number | null;
}

export function AudioPlayer({
  id,
  src,
  label,
  fallbackDurationSeconds = null,
}: AudioPlayerProps): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { register, claim, release } = useAudioPlayback();
  const seekId = useId();

  const [tocando, setTocando] = useState(false);
  const [posicao, setPosicao] = useState(0);
  const [duracao, setDuracao] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    const elemento = audioRef.current;
    if (elemento === null) {
      return undefined;
    }

    const aoTocar = (): void => {
      // Aqui, e so aqui, nasce a exclusividade do RNF17.
      claim(id);
      setTocando(true);
      setErro(false);
    };
    const aoPausar = (): void => {
      release(id);
      setTocando(false);
      setCarregando(false);
    };
    const aoTerminar = (): void => {
      release(id);
      setTocando(false);
      setPosicao(0);
    };
    const aoProgredir = (): void => {
      setPosicao(elemento.currentTime);
    };
    const aoSaberDuracao = (): void => {
      setDuracao(Number.isFinite(elemento.duration) ? elemento.duration : null);
    };
    const aoEsperar = (): void => {
      setCarregando(true);
    };
    const aoRetomar = (): void => {
      setCarregando(false);
    };
    const aoFalhar = (): void => {
      release(id);
      setTocando(false);
      setCarregando(false);
      setErro(true);
    };

    elemento.addEventListener('play', aoTocar);
    elemento.addEventListener('pause', aoPausar);
    elemento.addEventListener('ended', aoTerminar);
    elemento.addEventListener('timeupdate', aoProgredir);
    elemento.addEventListener('loadedmetadata', aoSaberDuracao);
    elemento.addEventListener('durationchange', aoSaberDuracao);
    elemento.addEventListener('waiting', aoEsperar);
    elemento.addEventListener('playing', aoRetomar);
    elemento.addEventListener('error', aoFalhar);

    const darBaixa = register(id, elemento);

    return () => {
      elemento.removeEventListener('play', aoTocar);
      elemento.removeEventListener('pause', aoPausar);
      elemento.removeEventListener('ended', aoTerminar);
      elemento.removeEventListener('timeupdate', aoProgredir);
      elemento.removeEventListener('loadedmetadata', aoSaberDuracao);
      elemento.removeEventListener('durationchange', aoSaberDuracao);
      elemento.removeEventListener('waiting', aoEsperar);
      elemento.removeEventListener('playing', aoRetomar);
      elemento.removeEventListener('error', aoFalhar);
      // Pausar na saida e nosso, nao do navegador. A especificacao manda pausar
      // um elemento de midia removido do documento, mas depender disso deixaria
      // a garantia do RNF17 nas maos da implementacao: se a lista rolar e este
      // cartao sair da tela enquanto toca, o som simplesmente para aqui. Os
      // listeners ja sairam acima, entao este pause nao dispara handler algum.
      elemento.pause();
      darBaixa();
    };
  }, [id, register, claim, release]);

  const alternar = useCallback((): void => {
    const elemento = audioRef.current;
    if (elemento === null) {
      return;
    }
    if (elemento.paused) {
      // Nada de `claim(id)` aqui: manter um unico ponto de decisao e o que faz
      // a regra valer tambem para o play que nao veio deste botao.
      void elemento.play().catch(() => {
        setErro(true);
      });
      return;
    }
    elemento.pause();
  }, []);

  const buscar = useCallback((evento: ChangeEvent<HTMLInputElement>): void => {
    const elemento = audioRef.current;
    const alvo = Number(evento.target.value);
    if (elemento === null || !Number.isFinite(alvo)) {
      return;
    }
    elemento.currentTime = alvo;
    setPosicao(alvo);
  }, []);

  // Duracao dos metadados vence a do backend; antes do primeiro play so existe a
  // segunda, e sem nenhuma das duas o controle de posicao fica desabilitado em
  // vez de fingir uma barra de 0 segundo.
  const duracaoEfetiva =
    duracao !== null && duracao > 0
      ? duracao
      : fallbackDurationSeconds !== null && fallbackDurationSeconds > 0
        ? fallbackDurationSeconds
        : 0;

  const proporcao = duracaoEfetiva > 0 ? Math.min(posicao / duracaoEfetiva, 1) : 0;

  return (
    <div className="flex w-full items-center gap-3">
      {/*
        Sem `controls`: a interface ao lado e o painel de controle. O elemento
        continua sendo a fonte da verdade -- todo evento de reproducao sai dele.

        Ele permanece montado mesmo no estado de erro. Desmonta-lo trocaria o
        conteudo do ref por null e deixaria um registro apontando para um
        elemento solto, o que e exatamente o tipo de inconsistencia que o
        registro explicito existe para evitar.
      */}
      {/* A gravacao mora no provedor de voz. A URL do painel nao viaja no
          Referer porque index.html declara <meta name="referrer"
          content="no-referrer"> e o backend manda Referrer-Policy igual. */}
      <audio ref={audioRef} src={src} preload="none" aria-label={label} />

      {erro ? (
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-status-critical" />
          Gravacao indisponivel.
        </p>
      ) : (
        <>
          <button
            type="button"
            onClick={alternar}
            aria-label={tocando ? `Pausar ${label}` : `Reproduzir ${label}`}
            aria-pressed={tocando}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand/10 text-brand transition-colors hover:bg-brand/20"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="currentColor"
              aria-hidden="true"
              focusable="false"
            >
              {tocando ? (
                <path d="M9 5.5h2.2v13H9zM12.8 5.5H15v13h-2.2z" />
              ) : (
                <path d="M8.5 5.6a.7.7 0 0 1 1.06-.6l8.2 5.4a.7.7 0 0 1 0 1.2l-8.2 5.4a.7.7 0 0 1-1.06-.6z" />
              )}
            </svg>
          </button>

          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {/*
              O range fica ANTES da trilha no DOM porque o `peer` do Tailwind so
              alcanca irmaos posteriores: e assim que o anel de foco aparece na
              barra mesmo com o controle real invisivel. Ele cobre toda a altura
              do bloco para dar alvo de toque confortavel no celular.
            */}
            <div className="relative flex h-6 items-center">
              <label htmlFor={seekId} className="sr-only">
                Posicao da {label}
              </label>
              <input
                id={seekId}
                type="range"
                min={0}
                max={duracaoEfetiva > 0 ? duracaoEfetiva : 1}
                step={0.1}
                value={posicao}
                onChange={buscar}
                disabled={duracaoEfetiva === 0}
                className="peer absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
              />
              <div className="pointer-events-none h-2 w-full overflow-hidden rounded-full bg-series-1/15 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand">
                <div
                  className="h-full rounded-full bg-series-1 transition-[width] duration-150"
                  style={{ width: `${(proporcao * 100).toFixed(2)}%` }}
                />
              </div>
            </div>

            <p className="flex items-center gap-2 text-xs text-ink-subtle">
              {/* tabular-nums: o relogio nao pode dancar a cada troca de digito. */}
              <span className="tabular-nums">
                {formatClock(posicao)} / {formatClock(duracaoEfetiva)}
              </span>
              {carregando ? <span className="text-ink-muted">carregando...</span> : null}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
